import React, { useEffect, useState } from "react";
import OwnerLayout from "@/components/OwnerLayout";
import { api, formatRupiah } from "@/lib/api";
import { Receipt, DollarSign, Package, TrendingUp, QrCode, Bell, Clock, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [tableQR, setTableQR] = useState("1");
  const [tableStatus, setTableStatus] = useState([]);

  const loadAll = async () => {
    const [s, r, t] = await Promise.all([
      api.get("/reports/summary?days=7"),
      api.get("/orders").catch(() => ({ data: [] })),
      api.get("/tables/status").catch(() => ({ data: [] })),
    ]);
    setSummary(s.data);
    setRecent((r.data || []).slice(0, 5));
    setTableStatus(t.data || []);
  };

  useEffect(() => {
    loadAll();
    const id = setInterval(loadAll, 8000);
    return () => clearInterval(id);
  }, []);

  const stats = [
    { label: "Transaksi (7 hari)", value: summary?.transaction_count ?? 0, icon: Receipt, testid: "stat-tx" },
    { label: "Omzet (7 hari)", value: formatRupiah(summary?.total_revenue ?? 0), icon: DollarSign, testid: "stat-revenue" },
    { label: "Item Terjual", value: summary?.total_items ?? 0, icon: Package, testid: "stat-items" },
    { label: "Rata-rata / Txn", value: formatRupiah((summary?.total_revenue || 0) / (summary?.transaction_count || 1)), icon: TrendingUp, testid: "stat-avg" },
  ];

  const customerLink = `${window.location.origin}/?table=${encodeURIComponent(tableQR)}`;
  const qrCodeImg = `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(customerLink)}`;

  return (
    <OwnerLayout>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-[#1E3A2B]">Ringkasan</h1>
        <p className="text-[#5C6A61] mt-1">Pantau performa kasir Anda dalam 7 hari terakhir</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid} className="bg-white p-5 rounded-md border border-[#D3CEC4]">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C6A61]">{s.label}</span>
              <s.icon className="w-4 h-4 text-[#C25934]" />
            </div>
            <div className="font-display text-2xl font-semibold text-[#1E3A2B]">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Live Table Status */}
      <div className="bg-white rounded-md border border-[#D3CEC4] p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B]">Status Meja Live</h2>
          <span className="text-xs text-[#5C6A61]">Auto-refresh 8 detik</span>
        </div>
        {tableStatus.length === 0 ? (
          <div className="text-sm text-[#5C6A61]">Belum ada meja terdaftar. Tambahkan di halaman QR Meja.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" data-testid="table-status-grid">
            {tableStatus.map((t) => {
              const hasCall = t.call_types && t.call_types.length > 0;
              const active = t.has_pending || t.has_paid;
              let bg = "bg-[#F9F9F6] border-[#D3CEC4]";
              let label = "Kosong";
              let Icon = CheckCircle2;
              let iconColor = "text-[#5C6A61]/50";
              if (hasCall) { bg = "bg-[#D4A373]/25 border-[#D4A373] pulse-ring"; label = "Panggil"; Icon = Bell; iconColor = "text-[#8a5a1f]"; }
              else if (t.has_pending) { bg = "bg-[#D64045]/10 border-[#D64045]"; label = "Menunggu"; Icon = Clock; iconColor = "text-[#D64045]"; }
              else if (t.has_paid) { bg = "bg-[#3A7D44]/10 border-[#3A7D44]"; label = "Diproses"; Icon = Receipt; iconColor = "text-[#3A7D44]"; }
              return (
                <div
                  key={t.table_number}
                  data-testid={`table-status-${t.table_number}`}
                  className={`${bg} border rounded-md p-3 text-center transition-colors`}
                >
                  <div className="flex items-center justify-center gap-1 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                  </div>
                  <div className="font-display text-lg font-semibold text-[#1E3A2B]" title={t.table_number}>
                    {t.table_number}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5C6A61]">{label}</div>
                  {active && <div className="text-xs font-medium text-[#C25934] mt-1">{formatRupiah(t.active_total)}</div>}
                  {hasCall && <div className="text-[10px] text-[#8a5a1f] mt-0.5">{t.call_types.length} panggilan</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-md border border-[#D3CEC4] p-6">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B] mb-4">Pesanan Terbaru</h2>
          <div className="space-y-2" data-testid="recent-orders">
            {recent.length === 0 && <div className="text-sm text-[#5C6A61]">Belum ada pesanan.</div>}
            {recent.map((o) => (
              <Link key={o.id} to="/owner/orders" className="flex items-center justify-between p-3 border border-[#EAE7DF] rounded-md hover:bg-[#F9F9F6]">
                <div>
                  <div className="font-medium text-[#1E3A2B]">#{o.order_no} · Meja {o.table_number}</div>
                  <div className="text-xs text-[#5C6A61]">{new Date(o.created_at).toLocaleString("id-ID")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-semibold text-[#1E3A2B]">{formatRupiah(o.total)}</div>
                  <div className="text-xs uppercase tracking-wider text-[#5C6A61]">{o.status}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-md border border-[#D3CEC4] p-6">
          <h2 className="font-display text-lg font-semibold text-[#1E3A2B] mb-2 flex items-center gap-2">
            <QrCode className="w-4 h-4" /> QR Meja
          </h2>
          <p className="text-xs text-[#5C6A61] mb-3">Pelanggan tinggal scan QR di meja untuk langsung melihat menu & memesan.</p>
          <input
            type="text"
            data-testid="input-table-qr"
            value={tableQR}
            onChange={(e) => setTableQR(e.target.value)}
            placeholder="Nomor meja"
            className="w-full border border-[#D3CEC4] bg-white rounded-xl px-3 py-2 mb-3 text-sm"
          />
          <img src={qrCodeImg} alt="QR Meja" className="w-full rounded-md border border-[#EAE7DF]" />
          <div className="mt-3 text-xs break-all text-[#5C6A61]" data-testid="customer-link">{customerLink}</div>
          <Link to="/owner/qr" data-testid="link-manage-qr" className="mt-3 inline-block text-sm text-[#C25934] font-medium hover:underline">
            Kelola & cetak QR semua meja →
          </Link>
        </div>
      </div>
    </OwnerLayout>
  );
}
